import {acceptHMRUpdate, defineStore} from 'pinia';
import {computed, ref} from 'vue';
import type {
	ExistingRiskResource,
	NewRiskResource,
	Risk,
	RiskDetailItem,
	RiskDetailResource,
	RiskDeviation
} from '~/lib/types/riskTypes';
import useApi from '~/composables/useApi';
import type {ProblemDetail} from '~/lib/types/fetchErrors';
import {levelDeviationLabels} from '~/composables/useCustomRiskLevelForm';
import {parseProblemDetail} from '~/middleware/api';
import {riskResourceFormLabels} from '~/composables/useRiskForm';
import {getFilenameFromContentDisposition, saveBlob} from '~/lib/download';

export const useRisksStore = defineStore('risks', () => {
	const {GET, PUT, POST, DELETE} = useApi();

	const activeRie = ref<string>();

	const risks = ref<Risk[]>([]);
	const riskDetails = ref<RiskDetailItem[]>([]);
	const riskTypes = ref<Risk['type'][]>([]);

	const loading = ref(false);
	const problemDetail = ref<ProblemDetail>();
	const waitingForExport = ref(false);

	const getRisk = computed(() => (riskId: number): Risk | undefined => {
		return risks.value.find((risk) => risk.id === riskId);
	});

	const clearData = () => {
		risks.value = [];
		riskDetails.value = [];
	};

	const fetchRiskTypes = () => {
		GET('/risks/types').then((response) => {
			const {data, error} = response;

			if (error || !data) {
				throw new Error('Could not load risk types: ' + JSON.stringify(error, null, 4));
			}

			riskTypes.value = data;
		});
	};

	const deleteRisk = async (rieId: string, riskId: number) => {
		loading.value = true;
		problemDetail.value = undefined;

		const {error} = await DELETE('/rie/{rieId}/risks/{riskId}', {
			params: {
				path: {
					rieId,
					riskId: riskId.toString()
				}
			}
		});

		if (error) {
			loading.value = false;
			problemDetail.value = error;
			return false;
		}

		loading.value = false;
		return true;
	};

	const fetchRiskOverview = async (rieId: string) => {
		loading.value = true;
		problemDetail.value = undefined;

		if (rieId !== activeRie.value) {
			clearData();
		}
		activeRie.value = rieId;

		const {data, error} = await GET('/rie/{rieId}/risks', {
			params: {
				path: {
					rieId
				}
			}
		});

		if (error || !data) {
			loading.value = false;
			problemDetail.value = error;
			return false;
		}

		risks.value = data;
		loading.value = false;
	};

	const updateRisk = async (riskId: number, risk: RiskDetailResource['risk'], questions?: RiskDetailResource['questions'], actions?: RiskDetailResource['actions']) => {
		const riskIndex = risks.value.findIndex((risk) => risk.id === riskId);
		if (riskIndex === -1) {
			risks.value.push(risk);
		} else {
			risks.value[riskIndex] = risk;
		}

		const riskDetailIndex = riskDetails.value.findIndex((detail) => detail.risk.id === riskId);
		if (riskDetailIndex === -1 && questions && actions) {
			riskDetails.value.push({risk, questions, actions});
		} else if (riskDetailIndex !== -1) {
			riskDetails.value[riskDetailIndex].risk = risk;

			if (questions) {
				riskDetails.value[riskDetailIndex].questions = questions;
			}

			if (actions) {
				riskDetails.value[riskDetailIndex].actions = actions;
			}
		} else {
			throw new Error('Invalid risk detail change');
		}
	};

	const getActionsForRisk = (riskId: Risk['id']): RiskDetailItem['actions'] | undefined => {
		return riskDetails.value.find((risk) => risk.risk.id === riskId)?.actions;
	};

	const getQuestionsForRisk = (riskId: Risk['id']): RiskDetailItem['questions'] => {
		return riskDetails.value.find((risk) => risk.risk.id === riskId)?.questions ?? [];
	};

	const fetchRisk = async (rieId: string, riskId: number) => {
		loading.value = true;
		problemDetail.value = undefined;

		if (rieId !== activeRie.value) {
			clearData();
		}
		activeRie.value = rieId;

		const {data, error} = await GET('/rie/{rieId}/risks/{riskId}', {
			params: {
				path: {
					rieId,
					riskId: riskId.toString()
				}
			}
		});

		if (error || !data) {
			loading.value = false;
			problemDetail.value = error;
			return false;
		}

		await updateRisk(riskId, data.risk, data.questions, data.actions);

		loading.value = false;
		return true;
	};

	const submitCustomRisk = async (rieId: string, risk: ExistingRiskResource | NewRiskResource) => {
		loading.value = true;
		problemDetail.value = undefined;

		if (risk.tag === 'EXISTING_RISK') {
			const {data, error} = await PUT('/rie/{rieId}/risks/{riskId}', {
				params: {
					path: {
						rieId,
						riskId: risk.id.toString()
					}
				},
				body: risk
			});

			if (error || !data) {
				problemDetail.value = parseProblemDetail(error, riskResourceFormLabels);
				loading.value = false;
				return false;
			}

			await updateRisk(data.id, data, [], []);
		} else if (risk.tag === 'NEW_RISK') {
			const {data, error} = await POST('/rie/{rieId}/risks', {
				params: {
					path: {
						rieId: rieId.toString()
					}
				},
				body: risk
			});

			if (error || !data) {
				problemDetail.value = parseProblemDetail(error, riskResourceFormLabels);
				loading.value = false;
				return false;
			}

			await updateRisk(data.id, data, [], []);
		} else {
			throw new Error('Invalid risk tag');
		}

		loading.value = false;
		return true;
	};

	const resetRiskDeviation = async (rieId: string, riskId: Risk['id']) => {
		const risk = risks.value.find((risk) => risk.id === riskId);
		if (risk) {
			const {data, error} = await DELETE('/rie/{rieId}/risks/{riskId}/risk-level/deviate', {
				params: {
					path: {
						rieId,
						riskId: riskId.toString()
					}
				}
			});

			if (error || !data) {
				loading.value = false;
				problemDetail.value = error;
				return false;
			}

			await updateRisk(riskId, data);
			loading.value = false;
			return true;
		}
	};

	const updateRiskDeviation = async (rieId: string, riskId: Risk['id'], deviation: RiskDeviation) => {
		const risk = risks.value.find((risk) => risk.id === riskId);
		if (risk) {

			const {data, error} = await PUT('/rie/{rieId}/risks/{riskId}/risk-level/deviate', {
				params: {
					path: {
						rieId,
						riskId: riskId.toString()
					}
				},
				body: deviation
			});

			if (error || !data) {
				loading.value = false;
				problemDetail.value = parseProblemDetail(error, levelDeviationLabels);
				return false;
			}

			await updateRisk(riskId, data);
			loading.value = false;
			return true;
		}
	};

	const updateRiskRootCauses = async (rieId: string, riskId: Risk['id'], riskRootCauses: Risk['riskRootCauses']) => {
		const risk = risks.value.find((risk) => risk.id === riskId);
		if (!risk) {
			throw new Error('Invalid risk');
		}

		const {data, error} = await PUT('/rie/{rieId}/risks/{riskId}/root-cause', {
			params: {
				path: {
					rieId,
					riskId: riskId.toString()
				}
			},
			body: riskRootCauses
		});

		if (error || !data) {
			loading.value = false;
			problemDetail.value = error;
			return false;
		}

		await updateRisk(riskId, data);
		loading.value = false;
		return true;
	};

	const exportRisks = async (rieId: string, riskIds: Array<Risk['id']>) => {
		if (waitingForExport.value) {
			return false;
		}
		waitingForExport.value = true;
		problemDetail.value = undefined;

		const {data, error, response} = await POST('/rie/{rieId}/risks/export', {
			params: {
				path: {
					rieId
				}
			},
			body: riskIds,
			parseAs: 'blob'
		});

		if (error || !data) {
			problemDetail.value = error;
			waitingForExport.value = false;
			return false;
		}

		const filename = getFilenameFromContentDisposition(response, 'Risico\'s.pdf');
		saveBlob(data, filename);
		waitingForExport.value = false;
	};

	const relevantRisks = computed(() => risks.value.filter(risk => risk.state === 'RELEVANT'));
	const irrelevantRisksWithActions = computed(() => {
		return risks.value.filter(({state, numberOfActions}) => {
			return state !== 'RELEVANT' && state !== 'HIDDEN' && numberOfActions > 0;
		});
	});

	return {
		risks,
		relevantRisks,
		irrelevantRisksWithActions,
		riskTypes,
		loading,
		problemDetail,
		getRisk,
		waitingForExport,
		getQuestionsForRisk,
		getActionsForRisk,
		fetchRiskOverview,
		fetchRisk,
		submitCustomRisk,
		resetRiskDeviation,
		updateRiskDeviation,
		updateRiskRootCauses,
		exportRisks,
		clearData,
		deleteRisk,
		fetchRiskTypes
	};
});

if (import.meta.hot) {
	import.meta.hot.accept(acceptHMRUpdate(useRisksStore, import.meta.hot));
}
